/**
 * CategorySelector Component
 *
 * Component for selecting categories and subcategories with pricing
 * Used in contractor profile form
 */

'use client';

import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import type { Category } from '../../../api';
import type { CategoryWithSubcategories } from './hooks/useCategoryManagement';

export interface CategorySelectorProps {
  selectedCategoryGroups: CategoryWithSubcategories[];
  rootCategories: Category[];
  getSubcategories: (parentId: string) => Category[];
  onAddCategory: () => void;
  onRemoveCategory: (index: number) => void;
  onCategoryChange: (index: number, categoryId: string) => void;
  onSubcategoryToggle: (groupIndex: number, subcategoryId: string) => void;
  onSubcategoryPriceChange: (
    groupIndex: number,
    subcategoryId: string,
    field: 'price' | 'unit',
    value: string
  ) => void;
}

export function CategorySelector({
  selectedCategoryGroups,
  rootCategories,
  getSubcategories,
  onAddCategory,
  onRemoveCategory,
  onCategoryChange,
  onSubcategoryToggle,
  onSubcategoryPriceChange,
}: CategorySelectorProps) {
  return (
    <div>
      <label className="flex items-center gap-1 mb-4">
        <Typography variant="body" weight="medium">
          Categories and Services (max 5 categories)
        </Typography>
        <span className="w-2 h-2 rounded-full bg-accent-error" />
      </label>

      <div className="space-y-6">
        {selectedCategoryGroups.map((group, groupIndex) => {
          const selectedCategory = rootCategories.find(
            (c) => c.id === group.categoryId
          );
          const subcategories = group.categoryId
            ? getSubcategories(group.categoryId)
            : [];

          return (
            <div
              key={groupIndex}
              className="p-6 border-2 border-border-primary rounded-2xl bg-white space-y-4"
            >
              {/* Category Selector */}
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <Typography variant="body" weight="medium" className="mb-2">
                    Category {groupIndex + 1}
                  </Typography>
                  <select
                    value={group.categoryId}
                    onChange={(e) => onCategoryChange(groupIndex, e.target.value)}
                    className="w-full px-5 py-3 border border-border-primary rounded-2xl focus:outline-none focus:ring-2 focus:ring-accent-primary bg-white"
                  >
                    <option value="">Select a category...</option>
                    {rootCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.nameEn}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => onRemoveCategory(groupIndex)}
                  className="p-3 hover:bg-background-secondary rounded-full transition-colors mt-7"
                  title="Remove category"
                >
                  <Icon name="x" size="sm" />
                </button>
              </div>

              {/* Subcategories */}
              {selectedCategory && subcategories.length > 0 && (
                <div className="pl-4 border-l-2 border-accent-secondary space-y-3">
                  <Typography variant="body" weight="medium">
                    Select subcategories and set prices:
                  </Typography>

                  {subcategories.map((subcat) => {
                    const isSelected = group.subcategories.some(
                      (s) => s.subcategoryId === subcat.id
                    );
                    const subcategoryData = group.subcategories.find(
                      (s) => s.subcategoryId === subcat.id
                    );

                    return (
                      <div
                        key={subcat.id}
                        className="p-4 bg-background-secondary rounded-xl space-y-3"
                      >
                        {/* Subcategory Checkbox */}
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              onSubcategoryToggle(groupIndex, subcat.id)
                            }
                            className="w-5 h-5 rounded border-border-primary text-accent-primary focus:ring-accent-primary"
                          />
                          <div className="flex-1">
                            <Typography variant="body" weight="medium">
                              {subcat.nameEn}
                            </Typography>
                            {subcat.descriptionEn && (
                              <Typography variant="bodySm" color="secondary">
                                {subcat.descriptionEn}
                              </Typography>
                            )}
                          </div>
                        </label>

                        {/* Price Input (shown only if selected) */}
                        {isSelected && (
                          <div className="grid grid-cols-2 gap-3 pl-8">
                            <div>
                              <label className="block mb-1">
                                <Typography variant="bodySm" weight="medium">
                                  Price
                                </Typography>
                              </label>
                              <input
                                type="number"
                                value={subcategoryData?.price || ''}
                                onChange={(e) =>
                                  onSubcategoryPriceChange(
                                    groupIndex,
                                    subcat.id,
                                    'price',
                                    e.target.value
                                  )
                                }
                                placeholder="0.00"
                                className="w-full px-4 py-2 border border-border-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-primary bg-white"
                              />
                            </div>
                            <div>
                              <label className="block mb-1">
                                <Typography variant="bodySm" weight="medium">
                                  Unit
                                </Typography>
                              </label>
                              <select
                                value={subcategoryData?.unit || 'per Hour'}
                                onChange={(e) =>
                                  onSubcategoryPriceChange(
                                    groupIndex,
                                    subcat.id,
                                    'unit',
                                    e.target.value
                                  )
                                }
                                className="w-full px-4 py-2 border border-border-primary rounded-xl focus:outline-none focus:ring-2 focus:ring-accent-primary bg-white"
                              >
                                <option value="per Hour">per Hour</option>
                                <option value="per Service">per Service</option>
                                <option value="per Project">per Project</option>
                                <option value="per Day">per Day</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedCategory && subcategories.length === 0 && (
                <div className="pl-4 py-3 text-center">
                  <Typography variant="bodySm" color="secondary">
                    No subcategories available for this category
                  </Typography>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Category Button */}
      {selectedCategoryGroups.length < 5 && (
        <button
          onClick={onAddCategory}
          className="w-full px-6 py-4 border-2 border-dashed border-accent-secondary text-accent-primary rounded-2xl hover:bg-background-secondary transition-colors flex items-center justify-center gap-2"
        >
          <Icon name="plus" size="sm" />
          <Typography variant="body" weight="medium" color="primary">
            Add Category
          </Typography>
        </button>
      )}

      <Typography variant="bodySm" color="secondary" className="mt-2">
        {selectedCategoryGroups.length}/5 categories added
      </Typography>
    </div>
  );
}
